import * as React from 'react';

/**
 * Simple error boundary for BlockNote editor to catch position resolution errors
 * Prevents infinite re-renders by only catching errors once
 */
export class BlockNoteErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    fallback?: React.ReactNode | (() => React.ReactNode);
    onError?: (error: Error, info: React.ErrorInfo) => void;
  },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: {
    children: React.ReactNode;
    fallback?: React.ReactNode | (() => React.ReactNode);
    onError?: (error: Error, info: React.ErrorInfo) => void;
  }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): Partial<{
    hasError: boolean;
    error: Error | null;
  }> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.warn('[BlockNoteEditor] Editor error caught:', error.message);

    if (this.props.onError) {
      this.props.onError(error, info);
    }
  }

  override render() {
    if (this.state.hasError) {
      if (typeof this.props.fallback === 'function') {
        return (this.props.fallback as () => React.ReactNode)();
      }
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
          Editor temporarily unavailable. Please refresh the page.
        </div>
      );
    }

    return this.props.children;
  }
}
